import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import App from './App.tsx';
/*
   IMPORTAÇÃO DIRETA DO ARQUIVO, NÃO DO BARRIL.

   `@/components/ui` reexporta os 20 componentes, e `@/features/cart` reexporta
   a gaveta do carrinho. Importar do barril aqui — que é o ponto de entrada da
   aplicação, sempre carregado — arrasta toda a biblioteca de componentes e todo
   o Radix para o pacote inicial, mesmo que o boot use dois deles.

   Custa duas linhas mais longas e economiza mais de 150 KB no primeiro
   carregamento, que é justamente o que decide se quem chegou pelo celular
   procurando preço de exame fica ou desiste.
*/
import { CartProvider } from '@/features/cart/CartContext';
import { ToastProvider } from '@/components/ui/Toast';

/**
 * Raiz da aplicação.
 *
 * Ordem dos provedores importa e não é arbitrária:
 *   QueryClient  →  precisa envolver tudo, porque o carrinho consulta o catálogo
 *   Toast        →  precisa envolver o carrinho, que avisa por toast quando um
 *                   item some do catálogo durante a reidratação
 *   Cart         →  o mais interno dos três, e ainda assim acima do roteador,
 *                   para o contador do cabeçalho sobreviver à navegação
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // O catálogo é praticamente estático — publicação é evento manual e raro.
      // Revalidar a cada foco de janela geraria requisição à toa e piscada de
      // preço na tela, que é justamente o que mina a confiança em loja de saúde.
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

/**
 * Monitoramento de erros — F1.4.1.
 *
 * Carregado sob demanda e só quando há endereço configurado, para não somar
 * peso ao pacote de quem apenas navega. O filtro de dados pessoais é
 * obrigatório: relatório de erro de um checkout carrega CPF, e-mail e telefone
 * no corpo da requisição, e isso não pode sair do domínio do laboratório.
 */
async function iniciarMonitoramento(): Promise<void> {
  const { env } = await import('@/lib/env');
  if (!env.VITE_SENTRY_DSN) return;

  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      tracesSampleRate: 0.1,
      beforeSend(evento: import('@sentry/react').ErrorEvent) {
        // Remove tudo que possa conter dado pessoal antes de sair da máquina.
        // Relatório de erro de checkout carrega CPF, e-mail e telefone no corpo
        // da requisição — e dado de saúde não sai do domínio do laboratório.
        if (evento.request) {
          delete evento.request.data;
          delete evento.request.cookies;
          delete evento.request.query_string;
        }
        delete evento.user;
        return evento;
      },
    });
  } catch {
    // Falta do pacote não pode derrubar o site. Monitoramento é apoio, não
    // requisito de funcionamento — e é opcional por decisão, não por descuido.
  }
}

void iniciarMonitoramento();

const raiz = document.getElementById('root');
if (!raiz) throw new Error('Elemento #root não encontrado no documento.');

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <CartProvider>
          <App />
        </CartProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
